import groupBy from 'lodash/groupBy';
import { getScreenData, Store } from 'cw-ui-core';
import { RESOURCES_TYPES } from 'src/features/resources-assignment/constants';
import { getPartnerId } from 'src/services/ApiService';
import {
    changeSelectAllResources,
    changeGrantAccessToAllFutureResources,
} from 'src/features/resources-assignment/slice';
import { hasFeature, Features } from 'src/util/featureToggles';

export const getInheritedContainers = (containers, selectedContainer) => {
    if (!containers || !selectedContainer) return [];
    // Get the containers that directly report to the given containerId
    const directReports = containers.filter(container => container.reportsTo?.id === selectedContainer);

    // Recursively find containers that report to the directReports
    const nestedReports =
        directReports?.length > 0
            ? directReports.flatMap(container => getInheritedContainers(containers, container.id))
            : [];

    // Combine direct reports and nested reports
    return [...directReports, ...nestedReports];
};

export const getInheritedCompaniesCount = containers => {
    if (!containers || containers.length === 0) return 0;

    // Calculate the total number of companies across all containers
    const companiesCount = containers.reduce((acc, container) => {
        if (container?.additionalInfo?.totalCompanies) {
            return acc + container.additionalInfo.totalCompanies;
        }
        return acc;
    }, 0);
    return companiesCount;
};

export const getInheritedSitesCount = containers => {
    if (!containers || containers.length === 0) return 0;

    // Calculate the total number of companies across all containers
    const sitesCount = containers.reduce((acc, container) => {
        if (container?.additionalInfo?.totalSites) {
            return acc + container.additionalInfo.totalSites;
        }
        return acc;
    }, 0);
    return sitesCount;
};

export const getContainerObject = (containers, containerId) => {
    const containerItem = containers.find(container => container.id === containerId);
    return containerItem ? { label: containerItem.name, value: containerItem.id } : { label: '', value: '' };
};

export const isCorporateStructureFeatureEnabled = () => {
    const partnerOptInStatus = getScreenData(Store().getState().screen, ['partnerOptInStatus'])?.toJS() || {};
    return partnerOptInStatus?.containerAssignmentEnabled && hasFeature(Features.CORPORATE_STRUCTURE);
};

export const getContainersByLevel = (containers, level) =>
    containers?.filter(container => container?.corpLevel?.level === level);

export const getMergedUniqueArray = (array1, array2) => {
    const uniqueArray = [...new Set([...array1, ...array2])];
    return uniqueArray;
};

export const getLevelByContainer = (containers, containerId) => {
    const container = containers?.find(container => container.id === containerId);
    return { value: container?.corpLevel?.level || '', label: container?.corpLevel?.name || '' };
};

export const checkIfRoleIsAdminRole = (_connectedAppId, screenKey, selectedRoleId) => {
    const connectedApps = getScreenData(Store().getState().screen, ['connectedApps'], screenKey)?.toJS() ?? [];
    const connectedApp = connectedApps?.find(app => app.connectedAppId === _connectedAppId);
    const roles = connectedApp?.authorization?.roleAssignment?.applicationRoles || [];

    const isAdmin = roles?.find(role => role?.roleId === selectedRoleId)?.isAdminRole;

    return isAdmin;
};

export const getAssignedResources = (assignedResources, _connectedAppId, isAdmin) => {
    let assignedCompanies = [];
    let assignedSites = [];
    let assignedCompaniesIds = [];
    let assignedSitesIds = [];
    let assignedContainersIds = [];
    let futureAllCompany = false;
    let futureAllSite = false;

    const partnerId = getPartnerId();

    if (isAdmin) {
        futureAllCompany = futureAllSite = true;
    } else {
        const grouped = groupBy(assignedResources, 'type');

        futureAllCompany = grouped[RESOURCES_TYPES.COMPANY]?.some(item => item?.id === partnerId) || false;
        futureAllSite = grouped[RESOURCES_TYPES.SITE]?.some(item => item?.id === partnerId) || false;

        if (grouped[RESOURCES_TYPES.CONTAINER]) {
            assignedContainersIds = grouped[RESOURCES_TYPES.CONTAINER]?.map(item => item?.id);
        }

        if (!futureAllCompany) {
            assignedCompanies = grouped[RESOURCES_TYPES.COMPANY] || [];
            if (assignedCompanies && assignedCompanies.length) {
                assignedCompaniesIds = assignedCompanies.map(x => x.id);
            }
        }
        if (!futureAllSite) {
            assignedSites = grouped[RESOURCES_TYPES.SITE] || [];
            if (assignedSites && assignedSites.length) {
                assignedSitesIds = assignedSites.map(x => x.id);
            }
        }
    }

    return {
        assignedCompaniesIds,
        assignedSitesIds,
        assignedCompanies,
        assignedSites,
        assignedContainersIds,
        futureAllCompany,
        futureAllSite,
    };
};

export const transformResourcesToRequiredFormat = resources => {
    if (!resources?.length) return [];

    return resources?.map(resource => {
        return {
            name: resource.resourceDisplayName || resource.resourceId,
            id: resource.resourceId,
            type: resource.resourceType,
            image: 'Computer_Screen',
        };
    });
};

export const getSelectedSitesCount = (
    selectAllResourcesCompany,
    selectAllResourcesSite,
    isAdminRole,
    selectedSites,
    totalRowCountSite,
    inheritedRowSite
) => {
    let selectedSiteCount = 0;
    if (isAdminRole || selectAllResourcesCompany) {
        selectedSiteCount = 0;
    } else if (selectAllResourcesSite) {
        selectedSiteCount = totalRowCountSite - inheritedRowSite.length;
    } else {
        selectedSiteCount = selectedSites?.length;
    }

    return selectedSiteCount;
};

export const getSelectedCompaniesCount = (
    selectAllResourcesCompany,
    isAdminRole,
    selectedCompanies,
    totalRowCountCompany,
    inheritedRowCompany
) => {
    let selectedCompanyCount;
    if (isAdminRole) {
        selectedCompanyCount = 0;
    } else if (selectAllResourcesCompany) {
        const difference = totalRowCountCompany - inheritedRowCompany?.length;
        selectedCompanyCount = difference > 0 ? difference : 0;
    } else {
        const length = selectedCompanies?.length;
        selectedCompanyCount = length > 0 ? length : 0;
    }

    return selectedCompanyCount;
};

export const getFilterModelWithItemsWithValueOnly = filterModel => {
    const items = filterModel.items.filter(x => {
        const value = String(x.value).trim();
        if (value === 'undefined' || value === 'null' || value === '') {
            return false;
        }
        return true;
    });
    const quickFilterValues = filterModel.quickFilterValues.filter(item => {
        const value = String(item).trim();
        if (value === 'undefined' || value === 'null' || value === '') {
            return false;
        }
        return true;
    });
    return {
        ...filterModel,
        items,
        quickFilterValues,
    };
};

const FILTERS_OPERATORS_MAPPING = {
    contains: ':',
    equals: '=',
    is: '=',
    '<': '<',
    '=': '=',
    '>': '>',
    '<=': '<=',
    '>=': '>=',
};

export const QUERY_LOGIC_OPERATORS = {
    and: 'AND',
    or: 'OR',
};

export const getParsedFiltersQueryStr = (filterModel, resourceType) => {
    // due to corporate structure check, need to define inside function
    const COMPANIES_FILTER_FIELDS_MAPPING = {
        name: 'name',
        friendlyName: 'friendlyName',
        primarySite: 'primarySite.name',
        status: 'activityStatus.activeFlag',
        ownershipType: 'ownershipType.name',
        totalSites: 'additionalInfo.totalSites',
        ...(isCorporateStructureFeatureEnabled() && {
            containerName: 'corpContainer.name',
        }),
    };

    const SITES_FILTER_FIELDS_MAPPING = {
        name: 'name',
        friendlyName: 'friendlyName',
        companyName: 'company.name',
        locations: 'primaryAddress.country',
        type: 'type.name',
        ...(isCorporateStructureFeatureEnabled() && {
            containerName: 'company.corpContainer.name',
        }),
    };

    const { logicOperator } = filterModel;

    const fieldsMapping =
        resourceType === RESOURCES_TYPES.COMPANY ? COMPANIES_FILTER_FIELDS_MAPPING : SITES_FILTER_FIELDS_MAPPING;

    const queryLogicalOperator = (logicOperator || QUERY_LOGIC_OPERATORS.or).toUpperCase();

    const mappedFilters = filterModel.items
        .map(filter => {
            const value = filter.value;
            if (value === undefined || value === null || value === '') {
                return undefined;
            }
            const field = fieldsMapping[filter.field] || filter.field;
            const operator = FILTERS_OPERATORS_MAPPING[filter.operator] || FILTERS_OPERATORS_MAPPING.contains;

            return `${field} ${operator} ${value}`;
        })
        .filter(Boolean)
        .join(` ${queryLogicalOperator} `);

    return mappedFilters;
};

export const getParsedQuickFiltersQueryStr = (filterModel, resourceType) => {
    // due to corporate structure check, need to define inside function
    const COMPANIES_QUICK_FILTER_FIELDS_MAPPING = {
        name: 'name',
        friendlyName: 'friendlyName',
        ownershipType: 'ownershipType.name',
        primarySite: 'primarySite.name',
        ...(isCorporateStructureFeatureEnabled() && {
            containerName: 'corpContainer.name',
        }),
    };

    const SITES_QUICK_FILTER_FIELDS_MAPPING = {
        name: 'name',
        friendlyName: 'friendlyName',
        companyName: 'company.name',
        locations: 'primaryAddress.country',
        type: 'type.name',
        ...(isCorporateStructureFeatureEnabled() && {
            containerName: 'company.corpContainer.name',
        }),
    };

    const fieldsMapping =
        resourceType === RESOURCES_TYPES.COMPANY
            ? COMPANIES_QUICK_FILTER_FIELDS_MAPPING
            : SITES_QUICK_FILTER_FIELDS_MAPPING;

    const mappedFilters = filterModel.quickFilterValues.map(filterValue => {
        return Object.keys(fieldsMapping)
            .map(field => {
                return `${fieldsMapping[field]} ${FILTERS_OPERATORS_MAPPING.contains} ${filterValue}`;
            })
            .join(` ${QUERY_LOGIC_OPERATORS.or} `);
    });
    return mappedFilters;
};

// filterModel = {
//     "items": [
//         {
//             "field": "name",
//             "operator": "contains",
//             "id": 99347,
//             "value": "abh",
//             "fromInput": ":r2c:"
//         }
//     ],
//     "logicOperator": "or",
//     "quickFilterValues": [],
//     "quickFilterLogicOperator": "and"
// }
// To be passed as filter=(name = Deadpools Ord AND compay.id = aaaasasasasasasas)
// Operators allowed to be filtered on: <, >, <=, >=, AND, OR, !=, =, :
// less than / greater than / less than equal to / greater than equal to / AND / OR/ not equal to / equal to / like
export const getFilterQueryString = (filterModel, resourceType) => {
    if (!filterModel) {
        return '';
    }
    let quickFiltersQueryString = '';
    if (filterModel.quickFilterValues.length) {
        quickFiltersQueryString = getParsedQuickFiltersQueryStr(filterModel, resourceType);
    }
    let filtersQueryString = '';
    if (filterModel.items.length) {
        filtersQueryString = getParsedFiltersQueryStr(filterModel, resourceType);
    }

    const finalQueryString = [quickFiltersQueryString, filtersQueryString]
        .filter(Boolean)
        .join(` ${QUERY_LOGIC_OPERATORS.or} `);

    return finalQueryString ? `(${finalQueryString})` : '';
};

export const getSortQueryString = sortModel => {
    if (!sortModel) {
        return '';
    }
    const sortQueryStr = sortModel.map(sort => `${sort.field} ${sort.sort}`).join(',');
    return sortQueryStr;
};

export const getDataGridContainerHeight = (elementsHeights, isUpThanXlScreen) => {
    const heightToSubstract =
        elementsHeights.resourcesAssignmentHeaderHeight +
        elementsHeights.resourcesAssignmentFooterHeight +
        elementsHeights.componentHeaderHeight +
        (isUpThanXlScreen ? 24 : 16);
    return `calc(100vh - ${heightToSubstract}px)`;
};

export const selectAllCompanies = () => {
    Store().dispatch(
        changeSelectAllResources({
            resourceType: RESOURCES_TYPES.COMPANY,
            value: true,
        })
    );
};

export const deselectAllCompanies = () => {
    Store().dispatch(
        changeSelectAllResources({
            resourceType: RESOURCES_TYPES.COMPANY,
            value: false,
        })
    );
    Store().dispatch(
        changeGrantAccessToAllFutureResources({
            resourceType: RESOURCES_TYPES.COMPANY,
            value: false,
        })
    );
};

export const selectAllSites = () => {
    Store().dispatch(
        changeSelectAllResources({
            resourceType: RESOURCES_TYPES.SITE,
            value: true,
        })
    );
};

export const deselectAllSites = () => {
    Store().dispatch(
        changeSelectAllResources({
            resourceType: RESOURCES_TYPES.SITE,
            value: false,
        })
    );
    Store().dispatch(
        changeGrantAccessToAllFutureResources({
            resourceType: RESOURCES_TYPES.SITE,
            value: false,
        })
    );
};

export const updateFutureAllCompany = value => {
    Store().dispatch(
        changeGrantAccessToAllFutureResources({
            resourceType: RESOURCES_TYPES.COMPANY,
            value,
        })
    );
};

export const updateFutureAllSite = value => {
    Store().dispatch(
        changeGrantAccessToAllFutureResources({
            resourceType: RESOURCES_TYPES.SITE,
            value,
        })
    );
};
